/**
 * hCaptcha widget primitive — the bot gate the password rails depend on.
 *
 * It exists because the edge already verifies a token the client never
 * produced: `HCaptchaVerifier.verify` (`apps/backend/src/auth/captcha.ts`)
 * returns `false` on an empty token, and the composition roots swap the
 * permissive `NullCaptchaVerifier` for the real one the moment `HCAPTCHA_SECRET`
 * is set. So on the day that secret is sealed, every password sign-up and login
 * 403s unless something on this side renders a challenge. This is that something.
 *
 * Props in, token out — no network of our own, no domain logic. The host app
 * decides WHETHER to render it (a build with no sitekey renders nothing and the
 * edge's `NullCaptchaVerifier` waves the request through, which is the local
 * loop) and owns the resulting token; this only owns the widget's lifecycle.
 *
 * The vendor script is loaded ONCE per document, lazily, and only when a Captcha
 * actually mounts — the front door is the only surface that needs it, so a
 * signed-in session never pays for it. `render=explicit` (rather than hCaptcha's
 * implicit auto-render) is required here: the widget host is created by Solid
 * after the script has already scanned the DOM, so auto-render would find
 * nothing.
 */
import { createEffect, createSignal, type JSX, on, onCleanup, onMount, Show } from 'solid-js';
import { CaptchaLoadError } from '../errors/index.ts';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

const STYLE_ID = 'f4-captcha';
const CSS = `
.f4-captcha { display: flex; flex-direction: column; gap: var(--space-2); }
.f4-captcha__error { color: var(--color-danger); font-size: var(--font-size-sm); margin: 0; }
`;

/** The global callback name hCaptcha invokes once `render=explicit` is ready. */
const READY_CALLBACK = '__f4HcaptchaReady';
const SCRIPT_SRC = `https://js.hcaptcha.com/1/api.js?render=explicit&onload=${READY_CALLBACK}`;

/** The vendor widget's palette. Mirrors `ResolvedTheme` so an app can pass `theme()` straight through. */
export type CaptchaTheme = 'light' | 'dark';

/**
 * How long to wait for the vendor script to call back before giving up.
 *
 * `error` alone is not enough. A content blocker that rewrites
 * `js.hcaptcha.com` typically answers 200 with a stub body: no `error` event
 * fires, `READY_CALLBACK` is never invoked, and the promise would stay pending
 * forever — leaving a disabled submit button with no explanation, the exact
 * outcome this component exists to avoid.
 */
const LOAD_TIMEOUT_MS = 15_000;

/** The subset of hCaptcha's `render` options this component sets. */
interface HCaptchaRenderOptions {
  readonly sitekey: string;
  readonly theme?: CaptchaTheme;
  /** Solved — receives the response token to send as `captchaToken`. */
  readonly callback: (token: string) => void;
  /** The solve aged out; the held token is no longer accepted by `siteverify`. */
  readonly 'expired-callback': () => void;
  /** The challenge itself failed (vendor outage, network). */
  readonly 'error-callback': () => void;
}

/** The slice of `window.hcaptcha` this component uses. */
interface HCaptchaApi {
  render(host: HTMLElement, options: HCaptchaRenderOptions): string;
  /** Discard the current solve and re-arm the widget for another attempt. */
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

/** The two globals the vendor script installs, typed so nothing here needs `any`. */
interface HCaptchaGlobals {
  hcaptcha?: HCaptchaApi;
  [READY_CALLBACK]?: () => void;
}

function globals(): HCaptchaGlobals {
  return globalThis as unknown as HCaptchaGlobals;
}

/**
 * The in-flight (or settled) script load, shared by every Captcha in the
 * document. Two mounted widgets — or a remount after a failed login — must not
 * append a second `<script>`: hCaptcha installs a single global, and a second
 * copy re-fires the ready callback under the first one's promise.
 */
let loader: Promise<HCaptchaApi> | undefined;

/**
 * Resolve the vendor API, injecting the script on first use.
 *
 * An already-present `window.hcaptcha` short-circuits — that is both the
 * second-mount path and the seam tests use to run with no network.
 */
function loadHCaptcha(timeoutMs: number): Promise<HCaptchaApi> {
  const present = globals().hcaptcha;
  if (present) {
    return Promise.resolve(present);
  }
  loader ??= new Promise<HCaptchaApi>((resolve, reject) => {
    const timer = setTimeout(() => {
      loader = undefined;
      reject(new CaptchaLoadError('the vendor script did not initialise in time'));
    }, timeoutMs);
    globals()[READY_CALLBACK] = () => {
      clearTimeout(timer);
      const api = globals().hcaptcha;
      if (api) {
        resolve(api);
      } else {
        reject(new CaptchaLoadError('the vendor script loaded but installed no widget api'));
      }
    };
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    // Fail LOUDLY rather than leaving the promise pending forever: a blocked or
    // offline script must surface as a visible error under the form, not as a
    // submit button that is disabled with no explanation.
    script.addEventListener('error', () => {
      clearTimeout(timer);
      loader = undefined;
      reject(new CaptchaLoadError('the vendor script could not be loaded'));
    });
    document.head.appendChild(script);
  });
  return loader;
}

export interface CaptchaProps {
  /** The site key for THIS deployment (build-time; public by design). */
  readonly sitekey: string;
  /**
   * The current token, or `null` whenever there is not a valid one — emitted on
   * solve, on expiry, on challenge error, and on a failed script load. The
   * caller gates its submit button on this being non-null.
   */
  readonly onToken: (token: string | null) => void;
  /**
   * Bump to discard the current solve and re-arm the widget.
   *
   * REQUIRED after any failed auth attempt. The edge verifies the captcha
   * BEFORE it checks credentials (`PasswordAuthService.register`/`login` call
   * `ensureHuman` first), and an hCaptcha token is single-use — so a login
   * refused for a wrong password has already spent the token. Resubmitting the
   * same one fails the bot gate instead of the password, and every retry after
   * the first mistake answers 403 with no way out but a page reload.
   */
  readonly resetKey?: number;
  /** Widget palette; pass the app's resolved theme so it is not light-on-dark. */
  readonly theme?: CaptchaTheme;
  /** Shown when the vendor script cannot load (required — no bare literal here). */
  readonly errorLabel: string;
  /**
   * Override the vendor-script load deadline. Injected the same way and for the
   * same reason `HCaptchaVerifier` injects `fetch` (DIP): the give-up path is
   * behaviour worth testing, and a suite must not spend the real deadline to do
   * it. Defaults to {@link LOAD_TIMEOUT_MS}.
   */
  readonly loadTimeoutMs?: number;
  readonly class?: string;
}

export function Captcha(props: CaptchaProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);

  const [failed, setFailed] = createSignal(false);
  let host!: HTMLDivElement;
  let api: HCaptchaApi | undefined;
  let widgetId: string | undefined;
  // Guards the async render against a unmount that lands first: `remove` on an
  // id that was never returned throws inside the vendor script.
  let disposed = false;

  onMount(() => {
    void loadHCaptcha(props.loadTimeoutMs ?? LOAD_TIMEOUT_MS)
      .then((loaded) => {
        if (disposed) {
          return;
        }
        api = loaded;
        widgetId = loaded.render(host, {
          sitekey: props.sitekey,
          ...(props.theme ? { theme: props.theme } : {}),
          callback: (token: string) => props.onToken(token),
          'expired-callback': () => props.onToken(null),
          'error-callback': () => props.onToken(null),
        });
      })
      .catch(() => {
        if (!disposed) {
          setFailed(true);
          props.onToken(null);
        }
      });
  });

  // `defer` so the first render does not reset a widget that has not been drawn
  // yet; only a genuine bump re-arms it.
  createEffect(
    on(
      () => props.resetKey,
      () => {
        if (api && widgetId !== undefined) {
          api.reset(widgetId);
          props.onToken(null);
        }
      },
      { defer: true },
    ),
  );

  onCleanup(() => {
    disposed = true;
    if (api && widgetId !== undefined) {
      api.remove(widgetId);
    }
  });

  return (
    <div class={cx('f4-captcha', props.class)}>
      <div ref={host} />
      <Show when={failed()}>
        <p class="f4-captcha__error" role="alert">
          {props.errorLabel}
        </p>
      </Show>
    </div>
  );
}
